import styled, { keyframes, css } from "styled-components";

// ── Animations ─────────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Page wrapper ───────────────────────────────────────────────────────────────
export const PageWrap = styled.div`
  padding: 60px 0 80px;
`;

// ── Search bar ─────────────────────────────────────────────────────────────────
export const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  margin-bottom: 24px;

  &:focus-within {
    border-color: #2f67d8;
    box-shadow: 0 2px 12px rgba(47,103,216,0.12), 0 0 0 3px rgba(47,103,216,0.07);
  }
`;

export const SearchIconWrap = styled.div`
  display: flex;
  align-items: center;
  padding: 0 14px 0 16px;
  color: #94a3b8;
  flex-shrink: 0;
  transition: color 0.15s ease;

  ${SearchWrap}:focus-within & {
    color: #2f67d8;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  height: 52px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-family: rebondG-Medium;
  color: #0f172a;
  padding: 0 8px 0 0;

  &::placeholder {
    color: #b0bec5;
    font-family: rebondG-Regular;
  }
`;

export const SearchBtn = styled.button`
  height: 52px;
  padding: 0 28px;
  background: linear-gradient(135deg, #2f67d8 0%, #1a4fad 100%);
  border: none;
  color: #fff;
  font-size: 14px;
  font-family: rebondG-Semi-Bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.15s ease, transform 0.12s ease;
  letter-spacing: 0.2px;

  &:hover {
    opacity: 0.92;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 480px) {
    padding: 0 18px;
    font-size: 13px;
  }
`;

// ── Toolbar (title + controls row) ────────────────────────────────────────────
export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 620px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

export const TableTitle = styled.h3`
  font-size: 22px;
  font-family: rebondG-Bold;
  color: #0f172a;
  margin: 0;
  flex: 1;

  @media (max-width: 620px) {
    font-size: 18px;
  }
`;

export const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  white-space: nowrap;
`;

export const ControlLabel = styled.span`
  font-size: 13px;
  font-family: rebondG-Medium;
  color: #64748b;
  white-space: nowrap;
`;

export const StyledSelect = styled.select`
  height: 28px;
  padding: 0 20px 0 4px;
  border: none;
  border-bottom: 1.5px solid #e2e8f0;
  border-radius: 0;
  background: transparent url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='5' viewBox='0 0 9 5'%3E%3Cpath fill='%2364748b' d='M0 0l4.5 5L9 0z'/%3E%3C/svg%3E") no-repeat right 2px center;
  -webkit-appearance: none;
  appearance: none;
  font-family: rebondG-Semi-Bold;
  font-size: 13px;
  color: #0f172a;
  cursor: pointer;
  transition: border-color 0.15s ease;
  min-width: 56px;

  &:focus {
    outline: none;
    border-bottom-color: #2f67d8;
  }
`;

export const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: #e2e8f0;
`;

export const ExportExcelButton = styled.button`
  align-items: center;
  background: linear-gradient(135deg, #16834f 0%, #0f6b40 100%);
  border: 1px solid #0f6b40;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(15, 107, 64, .16);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font: 12px rebondG-Semi-Bold;
  gap: 7px;
  height: 36px;
  padding: 0 14px;
  transition: transform .15s ease, box-shadow .15s ease, opacity .15s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    box-shadow: 0 7px 18px rgba(15, 107, 64, .22);
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: wait;
    opacity: .62;
  }
`;

// ── Table card shell ───────────────────────────────────────────────────────────
export const TableCard = styled.div`
  background: #fff;
  border-radius: ${({ $embedded }) => $embedded ? "0" : "20px"};
  border: ${({ $embedded }) => $embedded ? "none" : "1px solid #e2e8f0"};
  overflow: hidden;
  box-shadow: ${({ $embedded }) => $embedded
    ? "none"
    : "0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)"};

  ${({ $embedded }) => $embedded && css`
    && th { padding-top: 10px; padding-bottom: 10px; }
    && td { padding-top: 9px; padding-bottom: 9px; }
  `}
`;

// ── Desktop table ──────────────────────────────────────────────────────────────
export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
`;

export const THead = styled.thead`
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  position: sticky;
  top: 0;
  z-index: 2;
`;

export const TH = styled.th`
  padding: 14px 16px;
  text-align: left;
  font-size: 11px;
  font-family: rebondG-Semi-Bold;
  color: rgba(255,255,255,0.65);
  text-transform: uppercase;
  letter-spacing: 0.7px;
  white-space: nowrap;
  border-bottom: none;

  &:first-child { padding-left: 20px; }
  &:last-child  { padding-right: 20px; }
`;

export const TBody = styled.tbody``;

export const TR = styled.tr`
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.12s ease;
  animation: ${fadeIn} 0.25s ease both;
  animation-delay: ${({ $idx }) => Math.min($idx * 30, 300)}ms;

  &:last-child { border-bottom: none; }

  &:hover {
    background: #fafbff;
  }
`;

export const TD = styled.td`
  padding: 14px 16px;
  font-size: 13px;
  color: #334155;
  vertical-align: middle;
  max-width: 220px;

  &:first-child { padding-left: 20px; }
  &:last-child  { padding-right: 20px; }
`;

// ── Cell atoms ─────────────────────────────────────────────────────────────────
export const RowIndex = styled.span`
  font-size: 12px;
  color: #94a3b8;
  font-family: rebondG-Medium;
`;

export const UserAvatar = styled.div`
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #f1f5f9;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const AvatarFallback = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2f67d8, #1a4fad);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-family: rebondG-Bold;
  color: #fff;
  flex-shrink: 0;
`;

export const UserNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const UserNameText = styled.span`
  font-size: 13px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  white-space: nowrap;
`;

export const EmailText = styled.span`
  font-size: 12px;
  color: #64748b;
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-family: rebondG-Semi-Bold;
  white-space: nowrap;

  ${({ $status }) => {
    const s = (($status) || "").toLowerCase();
    if (s === "activated" || s === "active")
      return css`background: #dcfce7; color: #15803d;`;
    if (s === "pending")
      return css`background: #fef3c7; color: #b45309;`;
    return css`background: #fee2e2; color: #b91c1c;`;
  }}

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

export const BalanceBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-family: rebondG-Semi-Bold;
  background: ${({ $val }) => +$val > 0 ? 'rgba(47,103,216,0.08)' : '#f1f5f9'};
  color: ${({ $val }) => +$val > 0 ? '#2f67d8' : '#94a3b8'};
`;

export const StatCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;

  span:first-child {
    font-size: 15px;
    font-family: rebondG-Bold;
    color: #0f172a;
  }
  span:last-child {
    font-size: 10px;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
`;

export const TestTitleWrap = styled.div`
  font-size: 12px;
  color: #475569;
  line-height: 1.5;
  max-width: 200px;
`;

export const DateText = styled.span`
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
`;

export const CountryText = styled.span`
  font-size: 12px;
  color: #475569;
`;

// ── Footer / pagination ────────────────────────────────────────────────────────
export const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $embedded }) => $embedded ? "9px 18px" : "14px 20px"};
  border-top: 1px solid #f1f5f9;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

export const FooterInfo = styled.span`
  font-size: 13px;
  color: #94a3b8;
  white-space: nowrap;
`;

export const PaginationGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const PageBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 1.5px solid ${({ $active }) => $active ? '#2f67d8' : '#e2e8f0'};
  background: ${({ $active }) => $active ? '#2f67d8' : '#fff'};
  color: ${({ $active }) => $active ? '#fff' : '#475569'};
  font-size: 13px;
  font-family: rebondG-Semi-Bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #2f67d8;
    color: #2f67d8;
    background: rgba(200,30,52,0.05);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

export const PageDots = styled.span`
  font-size: 13px;
  color: #94a3b8;
  padding: 0 4px;
`;

// ── Mobile card layout ─────────────────────────────────────────────────────────
export const MobileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const MobileCard = styled.div`
  background: #fff;
  border-bottom: 1px solid #f1f5f9;
  padding: 14px 16px;
  animation: ${fadeIn} 0.25s ease both;
  animation-delay: ${({ $idx }) => Math.min($idx * 40, 300)}ms;
  transition: background 0.12s ease;
  overflow: hidden;

  &:last-child { border-bottom: none; }
  &:active { background: #fafbff; }
`;

export const MobileCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
`;

export const MobileCardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const MobileCardName = styled.p`
  font-size: 15px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  margin: 0 0 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MobileCardEmail = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MobileCardBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

export const MobileCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`;

export const MobileCardField = styled.div`
  background: #f8fafc;
  border-radius: 10px;
  padding: 8px 10px;
  min-width: 0;

  ${({ $full }) => $full && `grid-column: 1 / -1;`}
`;

export const MobileFieldLabel = styled.p`
  font-size: 10px;
  font-family: rebondG-Semi-Bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  margin: 0 0 3px;
  white-space: nowrap;
`;

export const MobileFieldValue = styled.p`
  font-size: 13px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-word;
`;

export const MobileCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
`;

export const MobileCardDate = styled.span`
  font-size: 11px;
  color: #94a3b8;
`;

// ── Empty / loading states ─────────────────────────────────────────────────────
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  gap: 10px;
  color: #94a3b8;
  font-size: 14px;
`;
